/**
 * Søren — the BotChainCP bot. Reusable chat surface.
 * Used inside the floating HelpFab, on the standalone /ask page, and on the
 * landing page where signed-out visitors get a few questions before logging in.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, RefreshCw, Loader2, Lock, ArrowRight, User as UserIcon } from "lucide-react";
import api from "@/lib/api";

const SESSION_STORAGE_KEY = "hl_chat_session";

const SUGGESTED_MEMBER = [
  "What's the daily yield on a 360-day stake?",
  "How is V5 rank reached?",
  "Why is my CA balance showing zero?",
  "Should I buy hashrate or buy CA directly?",
  "How do I cash out USDT to my bank?",
];

// Signed out, questions are rationed — so offer the three that decide it.
const SUGGESTED_GUEST = [
  "What is BotChain CP, in plain English?",
  "Do you ever hold or manage my money?",
  "What do I need to start, and how much?",
];

export const SorenMark = ({ size = 28, className = "" }) => (
  <img
    src="/brand/botchain-mark.png"
    srcSet="/brand/botchain-mark.png 1x, /brand/botchain-mark@2x.png 2x"
    alt="Søren"
    width={size}
    height={size}
    className={className}
    style={{ width: size, height: size, objectFit: "contain", display: "block", flexShrink: 0 }}
  />
);

function Avatar({ role }) {
  if (role === "user") {
    return (
      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "rgba(15,18,38,0.08)", color: "#334155" }}>
        <UserIcon size={13} />
      </div>
    );
  }
  return <SorenMark size={28} />;
}

function renderMarkdown(text) {
  // Lightweight inline markdown: **bold**, `code`, paragraph + bullet handling.
  const lines = (text || "").split(/\r?\n/);
  return lines.map((ln, i) => {
    const isBullet = /^\s*[-•]\s+/.test(ln);
    const content = ln.replace(/^\s*[-•]\s+/, "");
    const html = content
      .replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>')
      .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded font-mono text-[11px]" style="background: rgba(15,18,38,0.06)">$1</code>');
    if (ln.trim() === "") return <div key={i} className="h-2" />;
    if (isBullet) {
      return (
        <div key={i} className="flex gap-2 items-start text-sm leading-snug">
          <span className="text-ink-3 mt-1">•</span>
          <span dangerouslySetInnerHTML={{ __html: html }} />
        </div>
      );
    }
    return <p key={i} className="text-sm leading-snug" dangerouslySetInnerHTML={{ __html: html }} />;
  });
}

export default function ChatPanel({ compact = false, guest = false }) {
  const [sessionId, setSessionId] = useState(() => { try { return localStorage.getItem(SESSION_STORAGE_KEY) || ""; } catch { return ""; } });
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [hydrating, setHydrating] = useState(false);
  const [left, setLeft] = useState(null);     // guest questions remaining
  const [locked, setLocked] = useState(false); // guest allowance used up
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;
    setHydrating(true);
    api.get(`/chat/${sessionId}/history`).then(({ data }) => {
      setHistory(data.history || []);
      if (typeof data.questions_left === "number") setLeft(data.questions_left);
      setLocked(!!data.locked);
    }).catch(() => {}).finally(() => setHydrating(false));
  }, [sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history, loading]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || loading || locked) return;
    setInput("");
    const optimistic = [...history, { role: "user", content: text, ts: new Date().toISOString() }];
    setHistory(optimistic);
    setLoading(true);
    try {
      const { data } = await api.post(`/chat`, { session_id: sessionId || null, message: text });
      setHistory(data.history || []);
      if (typeof data.questions_left === "number") setLeft(data.questions_left);
      setLocked(!!data.locked);
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        try { localStorage.setItem(SESSION_STORAGE_KEY, data.session_id); } catch {}
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Sorry — I hit an error. Try again in a moment.";
      setHistory((h) => [...h, { role: "assistant", content: detail, ts: new Date().toISOString() }]);
    } finally {
      setLoading(false);
    }
  };

  const reset = async () => {
    if (sessionId) {
      try { await api.delete(`/chat/${sessionId}`); } catch {}
    }
    setSessionId("");
    try { localStorage.removeItem(SESSION_STORAGE_KEY); } catch {}
    setHistory([]);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const empty = history.length === 0 && !hydrating;
  const suggested = guest ? SUGGESTED_GUEST : SUGGESTED_MEMBER;

  return (
    <div className={`flex flex-col ${compact ? "h-[480px]" : "h-full"}`} data-testid="chat-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "rgba(15,18,38,0.06)" }}>
        <div className="flex items-center gap-2">
          <SorenMark size={28} />
          <div className="leading-tight">
            <div className="font-display text-sm font-semibold tracking-tight" data-testid="soren-name">Søren</div>
            <div className="text-[10px] text-ink-3 font-mono">
              {guest && left !== null
                ? `${left} free question${left === 1 ? "" : "s"} left`
                : "BotChainCP bot · trained on the CaryPact docs"}
            </div>
          </div>
        </div>
        {history.length > 0 && !guest && (
          <button onClick={reset} className="text-ink-3 hover:text-ink p-1.5 rounded-lg hover:bg-white/60" title="Start new chat" data-testid="chat-reset"><RefreshCw size={13} /></button>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="chat-messages">
        {empty && (
          <div>
            {guest && (
              <p className="text-[12px] text-ink-2 leading-snug mb-3" data-testid="soren-guest-intro">
                Hi — I&rsquo;m Søren, the bot in the logo. Ask me anything basic about BotChain CP or CaryPact.
                You get a few questions without logging in, so make them count.
              </p>
            )}
            <div className="text-xs text-ink-3 mb-2">Try asking</div>
            <div className="flex flex-col gap-2">
              {suggested.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  className="text-left px-3 py-2 rounded-xl bg-white/60 hover:bg-white text-[12px] text-ink-2 hover:text-ink transition-all border"
                  style={{ borderColor: "rgba(15,18,38,0.08)" }}
                  data-testid={`chat-suggested-${q.slice(0, 14).replace(/\W+/g, '-')}`}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {history.map((m, i) => (
          <div key={i} className="flex gap-2.5 items-start fade-up" data-testid={`chat-msg-${m.role}-${i}`}>
            <Avatar role={m.role} />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider font-mono text-ink-3 mb-1">{m.role === "user" ? "You" : "Søren"}</div>
              <div className={`rounded-xl px-3 py-2 inline-block max-w-full ${m.role === "user" ? "bg-white/70" : "bg-gradient-to-br from-white/80 to-white/60"}`} style={{ border: "1px solid rgba(15,18,38,0.06)" }}>
                <div className="space-y-1">{renderMarkdown(m.content)}</div>
              </div>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-2.5 items-start" data-testid="chat-loading">
            <Avatar role="assistant" />
            <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/60 text-xs text-ink-3" style={{ border: "1px solid rgba(15,18,38,0.06)" }}>
              <Loader2 size={11} className="animate-spin" /> thinking…
            </div>
          </div>
        )}
      </div>

      {/* Input — or, once a visitor's questions are used up, the way in */}
      {locked ? (
        <div className="px-4 pt-3 pb-4 border-t" style={{ borderColor: "rgba(15,18,38,0.06)", background: "linear-gradient(135deg, rgba(64,96,255,0.06), rgba(107,79,255,0.06))" }} data-testid="soren-guest-locked">
          <div className="flex items-center gap-1.5 text-xs font-semibold mb-1">
            <Lock size={12} style={{ color: "var(--primary)" }} /> That&rsquo;s my free questions used up
          </div>
          <p className="text-[11px] text-ink-2 leading-snug mb-3">
            Log in and I can read your own wallets and numbers — and there&rsquo;s no limit in there.
          </p>
          <div className="flex flex-wrap gap-2">
            <Link to="/login" className="btn-primary" style={{ padding: "8px 14px", fontSize: 12 }} data-testid="soren-login-cta">
              Log in <ArrowRight size={13} />
            </Link>
            <a href="/#signup" className="btn-secondary" style={{ padding: "8px 14px", fontSize: 12 }} data-testid="soren-signup-cta">
              Request access
            </a>
          </div>
        </div>
      ) : (
        <div className="px-3 pt-2 pb-3 border-t" style={{ borderColor: "rgba(15,18,38,0.06)" }}>
          <div className="flex items-end gap-2 px-3 py-2 rounded-2xl bg-white/70 focus-within:bg-white" style={{ border: "1px solid rgba(15,18,38,0.10)" }}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              placeholder={guest ? "Ask Søren anything about getting started…" : "Ask about setup, formulas, VIP tiers, or what your $1k could do…"}
              className="flex-1 bg-transparent border-none outline-none resize-none text-sm leading-snug py-1 max-h-32"
              data-testid="chat-input"
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || loading}
              className="w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: "linear-gradient(135deg, #4060FF, #6B4FFF)" }}
              data-testid="chat-send"
              aria-label="Send"
            >
              <Send size={13} />
            </button>
          </div>
          <div className="text-[10px] text-ink-3 mt-1.5 text-center font-mono">
            Søren is an AI — he can be wrong. Verify with the manual or your sponsor before deploying real capital.
          </div>
        </div>
      )}
    </div>
  );
}
